import React, { useEffect, useMemo, useRef, useState } from "react";
import { toResult, type Result } from "@sequesign/verifier-browser";
import { parseTrustedWitnessKeys } from "@sequesign/lib/verify";

import { SCENARIO } from "../scenario/torbridge.js";
import { GIG_CATALOG, type GigCatalogEntry } from "../live/gig-catalog.js";
import { bundleToLoadedReceipt } from "../load/bundle.js";
import { runDemoVerification } from "../verify/run-demo.js";
import { runSerialized } from "../verify/serialize.js";
import { VerificationPanel } from "./VerificationPanel.js";
import { CollapsibleJson } from "./JsonView.js";

// The witness default, mirroring DEFAULT_WITNESS_BASE_URL in
// src/sdk/witness-client.ts. Hardcoded here rather than imported so the browser
// bundle never pulls the SDK seal path (and its Node built-ins) — the seal path
// only runs server-side in functions/api/run.ts.
const DEFAULT_WITNESS_BASE_URL = "https://witness.sequesign.com";

interface Credentials {
  accessCode: string;
  agentPrivateKeyPem: string;
  witnessBaseUrl: string;
  witnessApiKey: string;
  llmApiKey: string;
}

interface RunResponse {
  bundle?: Record<string, string>;
  // Surfaced on the success screen. The server already returns these; the
  // client used to ignore them.
  receiptId?: string;
  chainId?: string;
  conformant?: boolean;
  error?: string;
}

// The realistic-client flow is a small state machine:
//   form → running → sealed (success screen) → receipt (inspect) → verifying → verified
// The presenter fills a data-entry screen and hits Go; the backend agent seals;
// a success screen confirms the seal FIRST; "Inspect & verify" opens the full
// package and runs the in-browser external-mode verification.
type Phase = "form" | "running" | "sealed" | "receipt" | "verifying" | "verified";

const KEYS_FETCH_TIMEOUT_MS = 10_000;
const MAX_KEYS_BYTES = 128 * 1024;
const RUN_TIMEOUT_MS = 45_000;

function signalWithTimeout(outer: AbortSignal, timeoutMs: number) {
  const controller = new AbortController();
  const onAbort = () => controller.abort();
  if (outer.aborted) controller.abort();
  else outer.addEventListener("abort", onAbort, { once: true });
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  return {
    signal: controller.signal,
    done: () => {
      window.clearTimeout(timer);
      outer.removeEventListener("abort", onAbort);
    }
  };
}

async function readTextBounded(res: Response, maxBytes: number): Promise<string> {
  const reader = res.body?.getReader();
  if (!reader) {
    return res.text();
  }
  const chunks: Uint8Array[] = [];
  let received = 0;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    received += value.byteLength;
    if (received > maxBytes) {
      await reader.cancel();
      throw new Error("Witness keys document is too large.");
    }
    chunks.push(value);
  }
  const bytes = new Uint8Array(received);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return new TextDecoder().decode(bytes);
}

// Fetch the witness's published well-known keys — the EXTERNAL trust anchor.
// Tries the witness directly first; a witness that does not allow this origin
// via CORS (or stalls past the per-attempt deadline) makes that fetch fail in
// the browser, so fall back to the same-origin /api/witness-keys relay, which
// returns the witness's document byte-for-byte. Either way the keys are the
// witness's published ones and all signature verification happens here in the
// browser.
async function fetchWitnessKeys(witnessBaseUrl: string, unmountSignal: AbortSignal) {
  const base = witnessBaseUrl.replace(/\/+$/, "");
  let text: string;
  const direct = signalWithTimeout(unmountSignal, KEYS_FETCH_TIMEOUT_MS);
  try {
    const res = await fetch(`${base}/.well-known/sequesign/keys.json`, { signal: direct.signal });
    if (!res.ok) {
      throw new Error(`Could not fetch witness keys (HTTP ${res.status}).`);
    }
    text = await readTextBounded(res, MAX_KEYS_BYTES);
  } catch (err) {
    if (unmountSignal.aborted) {
      throw err;
    }
    const relay = signalWithTimeout(unmountSignal, KEYS_FETCH_TIMEOUT_MS);
    try {
      const res = await fetch(`/api/witness-keys?base=${encodeURIComponent(base)}`, {
        signal: relay.signal
      });
      const relayed = await readTextBounded(res, MAX_KEYS_BYTES);
      if (!res.ok) {
        let message = `Could not fetch witness keys (HTTP ${res.status}).`;
        try {
          const parsed = JSON.parse(relayed) as { error?: string };
          if (parsed.error) message = parsed.error;
        } catch {
          // keep the generic message
        }
        throw new Error(message);
      }
      text = relayed;
    } finally {
      relay.done();
    }
  } finally {
    direct.done();
  }
  const keys = parseTrustedWitnessKeys(text);
  if (keys.length === 0) {
    throw new Error("Witness well-known document contained no witness keys.");
  }
  return keys;
}

// The copy LivePanel needs. SCENARIO.live (Torbridge) and the Veralta scenario
// both satisfy it; the gig-specific field labels come from the catalog, not
// here. reviewInstruction is optional so a scenario can relabel the editable
// agent-prompt field (e.g. clinical) without every scenario having to.
export interface LivePanelCopy {
  title: string;
  intro: string[];
  contactNote: string;
  keyNote: string;
  runLabel: string;
  runningLabel: string;
  fields: {
    agentPem: string;
    witnessBaseUrl: string;
    witnessApiKey: string;
    llmApiKey: string;
  };
  reviewInstruction?: { label: string; placeholder: string; help: string };
}

const DEFAULT_REVIEW_INSTRUCTION_COPY = {
  label: "Review instruction (optional)",
  placeholder: "e.g. Flag any vendor not on the approved supplier list.",
  help: "Prepended to the structured review prompt and sealed verbatim into the receipt's evidence. It is part of the record, not a hidden side channel."
};

// A one-line description for each file in the sealed package, shown in the
// inspect view so the presenter can tell what each document is.
function fileSubtitle(path: string): string | undefined {
  if (path === "receipt.json") return "the sealed receipt the agent returned";
  if (path === "actions.jsonl") return "one JSON record per sealed action";
  // Deliberately neutral: what evidence contains is scenario-specific (the
  // clinical gigs seal only hashes; the finance gigs seal some values in the
  // clear), so this shared label must not claim "hashes only".
  if (path.startsWith("evidence/")) return "sealed evidence for one action";
  if (path.startsWith("keys/")) return "public key used to verify the signatures";
  if (path === "profile.json") return "the mandate the run was bound to";
  if (path === "params.json") return "the task parameters that were bound";
  return undefined;
}

// The authoritative seal time is the witness's SIGNED witnessed_at inside the
// receipt (latest across attestations), never the browser clock — a skewed or
// delayed client must not present its own time as a receipt fact. Returns null
// if the receipt carries no witness timestamp.
function witnessedAtFromBundle(bundle: Record<string, string>): string | null {
  try {
    const receipt = JSON.parse(bundle["receipt.json"] ?? "{}") as {
      witness_attestations?: Array<{ witnessed_at?: unknown }>;
    };
    const times = (receipt.witness_attestations ?? [])
      .map((a) => a?.witnessed_at)
      .filter((t): t is string => typeof t === "string");
    // Compare by parsed epoch, not lexicographically: ISO-8601 values with
    // different offsets/precision (e.g. "…+02:00" vs "…Z") don't sort by
    // string in chronological order. Keep the string of the latest instant.
    let latest: string | null = null;
    let latestMs = -Infinity;
    for (const t of times) {
      const ms = Date.parse(t);
      if (!Number.isNaN(ms) && ms > latestMs) {
        latestMs = ms;
        latest = t;
      }
    }
    return latest;
  } catch {
    return null;
  }
}

// Whether the receipt carries a witness transparency-log entry. WitnessAttestation.
// log_entry is OPTIONAL — a witness without a transparency log still seals and
// verifies — so the success screen only claims a retained log entry when one is
// actually present.
function witnessLoggedFromBundle(bundle: Record<string, string>): boolean {
  try {
    const receipt = JSON.parse(bundle["receipt.json"] ?? "{}") as {
      witness_attestations?: Array<{ log_entry?: unknown }>;
    };
    return (receipt.witness_attestations ?? []).some(
      (a) => a?.log_entry !== undefined && a.log_entry !== null
    );
  } catch {
    return false;
  }
}

// Minimal ZIP writer (STORE method, no compression). Dependency-free so both
// demo apps bundle it identically without a cross-package resolution on a zip
// library. Produces a standard .zip the hosted verifier accepts (upload) and
// the CLI accepts once extracted — the sealed package's real layout is
// preserved. UTF-8 filenames (bit 11); one entry per file; fixed 1980-01-01
// timestamp for determinism.
const CRC32_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = CRC32_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

function zipStorePackage(files: Record<string, string>): Blob {
  const enc = new TextEncoder();
  const u16 = (n: number) => new Uint8Array([n & 0xff, (n >>> 8) & 0xff]);
  const u32 = (n: number) =>
    new Uint8Array([n & 0xff, (n >>> 8) & 0xff, (n >>> 16) & 0xff, (n >>> 24) & 0xff]);
  const TIME = u16(0);
  const DATE = u16(0x21); // 1980-01-01
  const parts: Uint8Array[] = [];
  const central: Uint8Array[] = [];
  let offset = 0;
  const emit = (p: Uint8Array) => {
    parts.push(p);
    offset += p.length;
  };
  for (const [name, contents] of Object.entries(files)) {
    const nameBytes = enc.encode(name);
    const data = enc.encode(contents);
    const crc = crc32(data);
    const size = u32(data.length);
    const localStart = offset;
    // Local file header + data.
    emit(u32(0x04034b50)); // signature
    emit(u16(20)); // version needed
    emit(u16(0x0800)); // flags: UTF-8 filename
    emit(u16(0)); // method: store
    emit(TIME);
    emit(DATE);
    emit(u32(crc));
    emit(size); // compressed size
    emit(size); // uncompressed size
    emit(u16(nameBytes.length));
    emit(u16(0)); // extra length
    emit(nameBytes);
    emit(data);
    // Central directory header (buffered; appended after all locals).
    central.push(
      u32(0x02014b50), // signature
      u16(20), // version made by
      u16(20), // version needed
      u16(0x0800), // flags: UTF-8
      u16(0), // method: store
      TIME,
      DATE,
      u32(crc),
      size,
      size,
      u16(nameBytes.length),
      u16(0), // extra length
      u16(0), // comment length
      u16(0), // disk number start
      u16(0), // internal attrs
      u32(0), // external attrs
      u32(localStart), // local header offset
      nameBytes
    );
  }
  const centralStart = offset;
  const centralSize = central.reduce((s, p) => s + p.length, 0);
  const count = Object.keys(files).length;
  const eocd = [
    u32(0x06054b50), // end of central directory signature
    u16(0), // disk number
    u16(0), // disk with central dir
    u16(count),
    u16(count),
    u32(centralSize),
    u32(centralStart),
    u16(0) // comment length
  ];
  // Concatenate into one ArrayBuffer-backed Uint8Array (a plain Uint8Array is a
  // valid BlobPart, sidestepping the Uint8Array<ArrayBufferLike> vs BlobPart
  // typing).
  const all = [...parts, ...central, ...eocd];
  const total = all.reduce((s, p) => s + p.length, 0);
  const out = new Uint8Array(total);
  let pos = 0;
  for (const p of all) {
    out.set(p, pos);
    pos += p.length;
  }
  return new Blob([out], { type: "application/zip" });
}

// Default the data-entry fields from the selected gig's spec.
function defaultsFor(catalog: GigCatalogEntry[], gigId: string): Record<string, string> {
  const entry = catalog.find((g) => g.id === gigId);
  const out: Record<string, string> = {};
  for (const f of entry?.fields ?? []) out[f.key] = f.default;
  return out;
}

// The demo (Torbridge) renders <LivePanel/> with no props; the Veralta app
// passes its clinical catalog + copy. Everything task-specific comes through
// these props, so the panel itself stays scenario-agnostic.
export function LivePanel({
  catalog = GIG_CATALOG,
  copy = SCENARIO.live
}: {
  catalog?: GigCatalogEntry[];
  copy?: LivePanelCopy;
} = {}) {
  const reviewCopy = copy.reviewInstruction ?? DEFAULT_REVIEW_INSTRUCTION_COPY;

  const [gigId, setGigId] = useState<string>(catalog[0].id);
  const entry = useMemo(() => catalog.find((g) => g.id === gigId) ?? catalog[0], [catalog, gigId]);

  const [creds, setCreds] = useState<Credentials>({
    accessCode: "",
    agentPrivateKeyPem: "",
    witnessBaseUrl: DEFAULT_WITNESS_BASE_URL,
    witnessApiKey: "",
    llmApiKey: ""
  });
  const [reviewInstruction, setReviewInstruction] = useState("");
  const [fields, setFields] = useState<Record<string, string>>(() =>
    defaultsFor(catalog, catalog[0].id)
  );
  const [setupOpen, setSetupOpen] = useState(false);

  const [phase, setPhase] = useState<Phase>("form");
  const [bundle, setBundle] = useState<Record<string, string> | null>(null);
  // The witness base URL used to PRODUCE the current bundle, snapshotted at run
  // time. Verification must fetch trust keys from this witness, not from the
  // presenter-setup field (which stays editable and could be changed between
  // Run and Verify, checking a valid receipt against the wrong witness).
  const [runWitnessBase, setRunWitnessBase] = useState<string>("");
  const [result, setResult] = useState<Result | null>(null);
  const [conformant, setConformant] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Headline facts for the success screen (from the /api/run response). The
  // seal TIME is not stored here — it is read from the witness's signed
  // witnessed_at in the bundle at render time, never the browser clock.
  const [receiptId, setReceiptId] = useState<string | null>(null);
  const [chainId, setChainId] = useState<string | null>(null);

  // Keys live in React state only. Wipe on unmount + beforeunload.
  const credsRef = useRef(creds);
  credsRef.current = creds;
  const cancelledRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    cancelledRef.current = false;
    const wipe = () =>
      setCreds((c) => ({
        ...c,
        accessCode: "",
        agentPrivateKeyPem: "",
        witnessApiKey: "",
        llmApiKey: ""
      }));
    window.addEventListener("beforeunload", wipe);
    return () => {
      window.removeEventListener("beforeunload", wipe);
      cancelledRef.current = true;
      abortRef.current?.abort();
      credsRef.current = {
        ...credsRef.current,
        accessCode: "",
        agentPrivateKeyPem: "",
        witnessApiKey: "",
        llmApiKey: ""
      };
    };
  }, []);

  function clearRun() {
    setBundle(null);
    setResult(null);
    setConformant(null);
    setError(null);
    setReceiptId(null);
    setChainId(null);
  }

  function selectGig(id: string) {
    setGigId(id);
    setFields(defaultsFor(catalog, id));
    // A gig switch invalidates any prior run.
    setPhase("form");
    clearRun();
  }

  function resetRun() {
    setPhase("form");
    clearRun();
  }

  // Build the /api/run inputs object from the string field values, per the
  // gig's field spec (numbers parsed, csv split into an array).
  function buildInputs(): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const f of entry.fields) {
      const raw = fields[f.key] ?? "";
      if (f.type === "number") out[f.key] = Number(raw);
      else if (f.type === "boolean") out[f.key] = raw === "true";
      else if (f.type === "csv")
        out[f.key] = raw
          .split(",")
          .map((s) => s.trim())
          .filter((s) => s.length > 0);
      else out[f.key] = raw; // text, textarea, and select are sent as-is
    }
    return out;
  }

  async function go() {
    const controller = new AbortController();
    abortRef.current = controller;
    setError(null);
    setResult(null);
    setConformant(null);
    setBundle(null);
    setPhase("running");
    try {
      // Client-side field checks; the Function re-validates authoritatively.
      for (const f of entry.fields) {
        const raw = fields[f.key] ?? "";
        if (f.type === "number") {
          const n = Number(raw);
          if (!Number.isFinite(n) || n < (f.min ?? 0)) {
            throw new Error(`Enter a valid ${f.label.toLowerCase()}.`);
          }
        } else if (raw.trim().length === 0) {
          throw new Error(`Enter ${f.label.toLowerCase()}.`);
        }
      }

      const runAttempt = signalWithTimeout(controller.signal, RUN_TIMEOUT_MS);
      let res: Response;
      let body: RunResponse;
      try {
        res = await fetch("/api/run", {
          method: "POST",
          headers: { "content-type": "application/json" },
          signal: runAttempt.signal,
          body: JSON.stringify({
            gigId,
            accessCode: creds.accessCode || undefined,
            agentPrivateKeyPem: creds.agentPrivateKeyPem,
            witnessBaseUrl: creds.witnessBaseUrl,
            witnessApiKey: creds.witnessApiKey || undefined,
            llmApiKey: entry.usesReview ? creds.llmApiKey : undefined,
            reviewInstruction: entry.usesReview ? reviewInstruction || undefined : undefined,
            inputs: buildInputs()
          })
        });
        // Parse defensively. The Function replies with JSON, but an
        // infrastructure error (e.g. Cloudflare substitutes its own HTML error
        // page for a 5xx) can return non-JSON — surface a clean status message
        // instead of a raw "Unexpected token '<'" JSON-parse error.
        const raw = await res.text();
        try {
          body = raw ? (JSON.parse(raw) as RunResponse) : {};
        } catch {
          body = { error: `The live run failed (HTTP ${res.status}).` };
        }
      } catch (err) {
        if (!controller.signal.aborted && runAttempt.signal.aborted) {
          throw new Error("The live run timed out. Check the witness and try again.");
        }
        throw err;
      } finally {
        runAttempt.done();
      }
      if (!res.ok || !body.bundle) {
        throw new Error(body.error ?? `Live run failed (HTTP ${res.status}).`);
      }
      if (cancelledRef.current) return;
      // Show a success screen FIRST; inspecting + verifying is an explicit next
      // step. Pin the witness this bundle was produced against so a later edit
      // to the presenter-setup field cannot redirect verification to another
      // witness.
      setRunWitnessBase(creds.witnessBaseUrl);
      setBundle(body.bundle);
      setReceiptId(body.receiptId ?? null);
      setChainId(body.chainId ?? null);
      setPhase("sealed");
    } catch (err) {
      if (cancelledRef.current) return;
      setError(err instanceof Error ? err.message : String(err));
      setPhase("form");
    }
  }

  async function verify() {
    if (!bundle) return;
    const controller = abortRef.current ?? new AbortController();
    abortRef.current = controller;
    setError(null);
    setPhase("verifying");
    try {
      // Use the witness the bundle was produced against, NOT the (still
      // editable) presenter-setup field.
      const trustedWitnessKeys = await fetchWitnessKeys(runWitnessBase, controller.signal);
      // The install below clears/repopulates the SHARED virtual filesystem —
      // never after the panel has unmounted, or it races the recorded verifier.
      if (cancelledRef.current) return;
      const loaded = bundleToLoadedReceipt("live", bundle);
      const started = performance.now();
      // Serialized process-wide (shared virtual FS): never overlap a recorded
      // or hero-card verification the visitor may trigger after switching tabs.
      const { report } = await runSerialized(() =>
        runDemoVerification(loaded, { externalTrust: { trustedWitnessKeys } })
      );
      if (cancelledRef.current) return;
      const totalMs = performance.now() - started;
      const r = toResult(report, { totalMs, verifierVersion: "demo", commit: "live" });
      setResult(r);
      setConformant(r.conformant);
      setPhase("verified");
    } catch (err) {
      if (cancelledRef.current) return;
      setError(err instanceof Error ? err.message : String(err));
      setPhase("receipt");
    }
  }

  // Download the sealed package as a real .zip (receipt.json, actions.jsonl,
  // evidence/*, keys/*, profile.json, params.json), so it verifies with the
  // STANDARD tools — upload it to the hosted verifier, or extract it and run
  // the CLI. A flat JSON map would only reload in this demo's own in-browser
  // verifier, and the verifier's paste-bundle shape can't be used because the
  // receipt's evidence paths are not action-id-named; a zip preserves the real
  // package layout the verifier expects. Built entirely in the browser from the
  // bundle already in hand (no server round-trip, nothing stored).
  function downloadPackage() {
    if (!bundle) return;
    const url = URL.createObjectURL(zipStorePackage(bundle));
    const a = document.createElement("a");
    a.href = url;
    a.download = `sequesign-receipt-${receiptId ?? "package"}.zip`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  const setCred = (k: keyof Credentials) => (v: string) => setCreds((c) => ({ ...c, [k]: v }));
  const setField = (k: string) => (v: string) => setFields((f) => ({ ...f, [k]: v }));

  const busy = phase === "running" || phase === "verifying";

  // The package files, sorted with the receipt envelope first, for the inspect
  // view. The sealed bundle is a flat { relativePath: contents } map.
  const bundleFiles = bundle
    ? Object.keys(bundle).sort((a, b) => {
        if (a === "receipt.json") return -1;
        if (b === "receipt.json") return 1;
        return a.localeCompare(b);
      })
    : [];
  // The witness's signed seal time (never the browser clock); null if absent.
  const witnessedAt = bundle ? witnessedAtFromBundle(bundle) : null;
  // Only claim a retained log entry when the receipt actually carries one.
  const witnessLogged = bundle ? witnessLoggedFromBundle(bundle) : false;

  return (
    <div className="live-panel">
      <div className="live-panel-head">
        <h2>{copy.title}</h2>
        {copy.intro.map((line, i) => (
          <p key={i}>{line}</p>
        ))}
      </div>

      <div className="live-note live-note-contact">{copy.contactNote}</div>

      {/* --- Gig selector (Scenario 2) --- */}
      <div className="gig-picker" role="tablist" aria-label="Agent job">
        {catalog.map((g) => (
          <button
            key={g.id}
            role="tab"
            aria-selected={g.id === gigId}
            className={"gig-tab" + (g.id === gigId ? " gig-tab-active" : "")}
            onClick={() => selectGig(g.id)}
            disabled={busy}
          >
            {g.label}
          </button>
        ))}
      </div>
      <p className="gig-blurb">{entry.blurb}</p>

      {/* --- Data-entry screen --- */}
      <form
        className="live-form"
        onSubmit={(e) => {
          e.preventDefault();
          void go();
        }}
      >
        <fieldset className="live-fieldset" disabled={busy}>
          <legend>Task details</legend>
          {entry.fields.map((f) =>
            f.type === "boolean" ? (
              <label className="live-field live-field-check" key={f.key}>
                <input
                  type="checkbox"
                  checked={(fields[f.key] ?? "false") === "true"}
                  onChange={(e) => setField(f.key)(e.target.checked ? "true" : "false")}
                />
                <span>{f.label}</span>
                {f.help && <span className="live-field-help">{f.help}</span>}
              </label>
            ) : (
              <label className="live-field" key={f.key}>
                <span>{f.label}</span>
                {f.type === "textarea" ? (
                  <textarea
                    className="live-record"
                    rows={12}
                    spellCheck={false}
                    value={fields[f.key] ?? ""}
                    onChange={(e) => setField(f.key)(e.target.value)}
                  />
                ) : f.type === "select" ? (
                  <select
                    value={fields[f.key] ?? ""}
                    onChange={(e) => setField(f.key)(e.target.value)}
                  >
                    {(f.options ?? []).map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={f.type === "number" ? "number" : "text"}
                    min={f.type === "number" ? (f.min ?? 0) : undefined}
                    step={f.type === "number" ? "0.01" : undefined}
                    value={fields[f.key] ?? ""}
                    onChange={(e) => setField(f.key)(e.target.value)}
                  />
                )}
                {f.help && <span className="live-field-help">{f.help}</span>}
              </label>
            )
          )}
          <div className="live-note live-note-hint">{entry.violationHint}</div>
        </fieldset>

        {/* --- Presenter setup (collapsed): keys, access code, review prompt --- */}
        <details className="presenter-setup" open={setupOpen}>
          <summary
            className="presenter-setup-summary"
            onClick={(e) => {
              e.preventDefault();
              setSetupOpen((v) => !v);
            }}
          >
            Presenter setup: keys &amp; options
          </summary>
          <div className="presenter-setup-body">
            <label className="live-field">
              <span>Access code</span>
              <input
                type="password"
                autoComplete="off"
                value={creds.accessCode}
                onChange={(e) => setCred("accessCode")(e.target.value)}
                placeholder="only if this deployment requires one"
              />
            </label>
            <label className="live-field">
              <span>{copy.fields.agentPem}</span>
              <textarea
                rows={4}
                autoComplete="off"
                spellCheck={false}
                value={creds.agentPrivateKeyPem}
                onChange={(e) => setCred("agentPrivateKeyPem")(e.target.value)}
                placeholder={"-----BEGIN PRIVATE KEY-----\n…\n-----END PRIVATE KEY-----"}
              />
            </label>
            <label className="live-field">
              <span>{copy.fields.witnessBaseUrl}</span>
              <input
                type="text"
                autoComplete="off"
                value={creds.witnessBaseUrl}
                onChange={(e) => setCred("witnessBaseUrl")(e.target.value)}
              />
            </label>
            <label className="live-field">
              <span>{copy.fields.witnessApiKey}</span>
              <input
                type="password"
                autoComplete="off"
                value={creds.witnessApiKey}
                onChange={(e) => setCred("witnessApiKey")(e.target.value)}
              />
            </label>
            {entry.usesReview && (
              <>
                <label className="live-field">
                  <span>{copy.fields.llmApiKey}</span>
                  <input
                    type="password"
                    autoComplete="off"
                    value={creds.llmApiKey}
                    onChange={(e) => setCred("llmApiKey")(e.target.value)}
                  />
                </label>
                <label className="live-field">
                  <span>{reviewCopy.label}</span>
                  <textarea
                    rows={3}
                    value={reviewInstruction}
                    onChange={(e) => setReviewInstruction(e.target.value)}
                    placeholder={reviewCopy.placeholder}
                  />
                  <span className="live-field-help">{reviewCopy.help}</span>
                </label>
              </>
            )}
            <div className="live-note live-note-keys">{copy.keyNote}</div>
          </div>
        </details>

        <button type="submit" className="live-run-btn" disabled={busy}>
          {phase === "running" ? copy.runningLabel : (entry.runLabel ?? copy.runLabel)}
        </button>
      </form>

      {error && <div className="verify-error">{error}</div>}

      {/* --- Success screen: confirm the seal before inspecting --- */}
      {phase === "sealed" && bundle && (
        <div className="live-success">
          <div className="live-success-badge" aria-hidden="true">
            ✓
          </div>
          <h3 className="live-success-title">Sealed &amp; witnessed</h3>
          <p className="live-success-sub">
            The agent&apos;s work was countersigned by the configured witness and sealed into a
            verifiable receipt.
          </p>
          <dl className="live-success-facts">
            {receiptId && (
              <>
                <dt>Receipt</dt>
                <dd className="live-success-mono">{receiptId}</dd>
              </>
            )}
            {chainId && (
              <>
                <dt>Chain</dt>
                <dd className="live-success-mono">{chainId}</dd>
              </>
            )}
            {witnessedAt && (
              <>
                <dt>Witnessed</dt>
                <dd>{new Date(witnessedAt).toLocaleString()}</dd>
              </>
            )}
          </dl>
          <div className="live-success-note">
            Direct mode: you hold the receipt package and evidence; Sequesign does not store them.
            {witnessLogged && " The witness retains its signed log entry for this chain."}
          </div>
          <div className="live-success-actions">
            <button className="live-run-btn" onClick={() => setPhase("receipt")}>
              Inspect &amp; verify receipt
            </button>
            <button className="live-secondary-btn" onClick={downloadPackage}>
              Download package
            </button>
            <button className="live-secondary-btn" onClick={resetRun}>
              Run another
            </button>
          </div>
        </div>
      )}

      {/* --- Inspect the full package, then an explicit Verify step --- */}
      {bundle && (phase === "receipt" || phase === "verifying" || phase === "verified") && (
        <div className="live-receipt">
          <div className="live-receipt-head">
            <h3>Inspect the sealed package</h3>
            <p>
              This is exactly what the agent handed back, every file in the sealed package, before
              anyone checks it. Open any file, then verify it yourself.
            </p>
          </div>
          {bundleFiles.map((path) => (
            <CollapsibleJson
              key={path}
              title={path}
              subtitle={fileSubtitle(path)}
              json={bundle[path] ?? ""}
              defaultOpen={path === "receipt.json" && phase === "receipt"}
            />
          ))}
          <div className="live-receipt-actions">
            {phase !== "verified" && (
              <button className="live-run-btn" onClick={() => void verify()} disabled={busy}>
                {phase === "verifying" ? "Verifying…" : "Verify this receipt"}
              </button>
            )}
            <button className="live-secondary-btn" onClick={downloadPackage}>
              Download package
            </button>
          </div>
        </div>
      )}

      {conformant === false && phase === "verified" && (
        <div className="live-violation-note">
          The sealed receipt records conformant: false. The recorded work violated the mandate. It
          still verifies as authentic; the violation is recorded honestly.
        </div>
      )}

      {(phase === "verifying" || phase === "verified") && (
        <VerificationPanel
          result={result}
          loading={phase === "verifying"}
          error={null}
          breaks={[]}
          activeBreak={null}
          onBreak={() => undefined}
          onRestore={() => undefined}
        />
      )}

      {phase === "verified" && (
        <button className="live-secondary-btn" onClick={resetRun}>
          Run another
        </button>
      )}
    </div>
  );
}
